import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFilterContext } from '../context/FilterContext';

import { useGameContext } from '../context/GameContext';
import { getFilterSummary } from '../utils/filterUtils';
import TeamSearch from './TeamSearch';
import FilterModal from './FilterModal';
import SortButton from './SortButton';
import { isRankingsInfoForWeek } from '../utils/rankingsBannerUtils';
import styles from '../styles/IntegratedHeader.module.css';

const IntegratedHeader = ({
  displayedWeek,
  visibleWeeks,
  currentWeek,
  setDisplayedWeek,
  fetchWatchedPicks,
  handleCurrentWeek,
  getWeekLabel,
  games
}) => {
  const {
    filters,
    hasActiveFilters
  } = useFilterContext();



  const { rankingsInfo } = useGameContext();

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const lastScrollY = useRef(0);
  const controlsRef = useRef(null);
  const [isDocked, setIsDocked] = useState(true);
  const [scrollOffset, setScrollOffset] = useState(0);

  // Get filter summary for display
  const filterSummary = useMemo(() => {
    return getFilterSummary(filters);
  }, [filters]);

  const showRankingsBanner = isRankingsInfoForWeek(rankingsInfo, displayedWeek);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const headerHeight = 90; // Height of the main header

      if (currentScrollY <= 0) {
        // At the top - dock to header
        setIsDocked(true);
        setScrollOffset(0);
      } else if (currentScrollY >= headerHeight) {
        // Header out of view - stick to top
        setIsDocked(false);
        setScrollOffset(headerHeight);
      } else {
        // Header partially visible - follow scroll (move up as header scrolls up)
        setIsDocked(false);
        setScrollOffset(currentScrollY);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);



  return (
    <div
      className={`${styles.integratedHeader} ${isDocked ? styles.docked : styles.floating}`}
      style={{
        transform: isDocked ? 'translateY(0)' : `translateY(-${scrollOffset}px)`
      }}
      ref={controlsRef}
    >
      {/* Left side - Week Controls */}
      <div className={styles.weekControls}>
        <select
          className={styles.weekSelect}
          value={displayedWeek || ''}
          onChange={(e) => {
            const newWeek = Number(e.target.value);
            setDisplayedWeek(newWeek);
            fetchWatchedPicks(newWeek);
          }}
        >
          <option value="" disabled>Select a week</option>
          {visibleWeeks.map((week, index) => (
            <option key={`${week.week}-${week.season || index}`} value={week.week}>
              {getWeekLabel(week)}
            </option>
          ))}
        </select>
        <button
          className={`${styles.weekButton} ${styles.currentWeek}`}
          onClick={handleCurrentWeek}
          disabled={displayedWeek === currentWeek || currentWeek === null}
        >
          Current Week
        </button>
      </div>

      {/* Center - Active Filters Display (Desktop) */}
      <div className={styles.activeFiltersDisplay}>
        {hasActiveFilters && (
          <div className={styles.activeFilters}>
            {filterSummary.slice(0, 3).map((filter, index) => (
              <span key={`filter-${filter}-${index}`} className={styles.activeFilter}>
                {filter}
              </span>
            ))}
            {filterSummary.length > 3 && (
              <span className={styles.moreFilters}>
                +{filterSummary.length - 3} more
              </span>
            )}
          </div>
        )}
      </div>

      {/* Rankings Info Banner (only for the displayed pick-sheet week) */}
      {showRankingsBanner && !rankingsInfo.hasRankings && (
        <div className={styles.rankingsBanner}>
          <span className={styles.rankingsMessage}>
            Rankings Not Yet Available for Week {rankingsInfo.week}
          </span>
        </div>
      )}
      {showRankingsBanner && rankingsInfo.hasRankings && rankingsInfo.isFallback && (
        <div className={styles.rankingsBanner} style={{ backgroundColor: '#2d4a2d' }}>
          <span className={styles.rankingsMessage}>
            Showing Final CFP Rankings
          </span>
        </div>
      )}

      {/* Right side - Filter Controls */}
      <div className={styles.filterControls}>
        {/* Team Search */}
        <TeamSearch />

        {/* Sort Button */}
        <SortButton />

        {/* Filter Button */}
        <button
          className={`${styles.filterButton} ${hasActiveFilters ? styles.active : ''}`}
          onClick={() => setIsFilterModalOpen(true)}
          aria-label="Filters"
        >
          <span className={styles.filterText}>Filters</span>
          {hasActiveFilters && <span className={styles.filterBadge}></span>}
        </button>
      </div>

      {/* Filter Modal */}
      <FilterModal
        games={games}
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
      />
    </div>
  );
};

IntegratedHeader.propTypes = {
  displayedWeek: PropTypes.number,
  visibleWeeks: PropTypes.arrayOf(PropTypes.shape({
    week: PropTypes.number.isRequired,
  })).isRequired,
  currentWeek: PropTypes.number,
  setDisplayedWeek: PropTypes.func.isRequired,
  fetchWatchedPicks: PropTypes.func.isRequired,
  handleCurrentWeek: PropTypes.func.isRequired,
  getWeekLabel: PropTypes.func.isRequired,
  games: PropTypes.array.isRequired
};

export default IntegratedHeader;
